import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Clock, CalendarX, CalendarCheck, ArrowLeft, House, Receipt, X } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getDeptLabel } from '@/constants/departments';
import LucentLoader from '@/components/LucentLoader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucketModal, setBucketModal] = useState({ open: false, bucket: null, label: '', list: [], loading: false });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#655dc6', '#2563EB', '#059669', '#D97706', '#DC2626'];

  // Admin sees off_site_employees in the 4th slot of the top row instead
  // of "pending leaves" — admin doesn't sit in the leave approval chain in
  // the normal flow, so that card was noise for them. Other roles (GM,
  // finance manager) still see the leave-pending card.
  const isAdmin = (user?.role === 'admin');
  const statCards = isAdmin ? [
    { icon: Users, label: 'total_employees', value: stats?.total_employees || 0, color: 'bg-[#655dc6]' },
    { icon: Clock, label: 'present_today', value: stats?.present_today || 0, color: 'bg-[#059669]' },
    { icon: CalendarX, label: 'absent_today', value: stats?.absent_today || 0, color: 'bg-[#DC2626]' },
    // First-row 4th card for admin: how many employees are NOT subject to
    // attendance tracking (doctors + flexible/remote/consultant employees).
    { icon: Users, label: 'off_site_employees', value: stats?.off_site_employees || 0, color: 'bg-[#92400E]' },
    { icon: CalendarCheck, label: 'on_leave_today', value: stats?.on_leave_today || 0, color: 'bg-[#0891B2]' },
    { icon: CalendarCheck, label: 'rest_day_today', value: stats?.rest_day_today || 0, color: 'bg-[#7C3AED]' },
    { icon: Clock, label: 'not_yet_due_today', value: stats?.not_yet_due_today || 0, color: 'bg-[#475569]' },
    { icon: Receipt, label: 'pending_clearances', value: stats?.pending_clearances || 0, color: 'bg-[#10B981]', testid: 'stat-pending-clearances' },
  ] : [
    { icon: Users, label: 'total_employees', value: stats?.total_employees || 0, color: 'bg-[#655dc6]' },
    { icon: Clock, label: 'present_today', value: stats?.present_today || 0, color: 'bg-[#059669]' },
    { icon: CalendarX, label: 'absent_today', value: stats?.absent_today || 0, color: 'bg-[#DC2626]' },
    { icon: CalendarCheck, label: 'on_leave_today', value: stats?.on_leave_today || 0, color: 'bg-[#0891B2]' },
    { icon: CalendarCheck, label: 'rest_day_today', value: stats?.rest_day_today || 0, color: 'bg-[#7C3AED]' },
    { icon: Clock, label: 'not_yet_due_today', value: stats?.not_yet_due_today || 0, color: 'bg-[#475569]' },
    { icon: Users, label: 'off_site_employees', value: stats?.off_site_employees || 0, color: 'bg-[#92400E]' },
    { icon: CalendarCheck, label: 'pending_leaves', value: stats?.pending_leaves || 0, color: 'bg-[#D97706]' },
    { icon: Receipt, label: 'pending_clearances', value: stats?.pending_clearances || 0, color: 'bg-[#10B981]', testid: 'stat-pending-clearances' },
  ];

  const departmentData = stats?.departments?.map(d => ({
    name: getDeptLabel(d._id, language) || 'Other',
    value: d.count
  })) || [];

  const openBucket = async (bucket, label) => {
    setBucketModal({ open: true, bucket, label, list: [], loading: true });
    try {
      const { data } = await axios.get(`${API}/dashboard/bucket/${bucket}`, { withCredentials: true });
      setBucketModal(m => ({ ...m, list: data.employees || [], loading: false }));
    } catch (e) {
      setBucketModal(m => ({ ...m, list: [], loading: false }));
    }
  };

  // Map each card label → backend bucket key. Cards without a bucket are
  // navigation shortcuts to dedicated pages.
  const cardBucket = {
    total_employees: 'total',
    present_today: 'present',
    absent_today: 'absent',
    on_leave_today: 'on_leave',
    rest_day_today: 'rest_day',
    not_yet_due_today: 'not_yet_due',
    off_site_employees: 'off_site',
  };
  const cardNavigate = {
    pending_leaves: '/admin/leaves',
    pending_clearances: '/admin/clearance',
  };

  const handleCardClick = (stat) => {
    if (cardBucket[stat.label]) return openBucket(cardBucket[stat.label], t(stat.label));
    if (cardNavigate[stat.label]) return navigate(cardNavigate[stat.label]);
  };

  if (loading) {
    return <LucentLoader />;
  }

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="admin-dashboard" dir={direction}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <Button variant="ghost" onClick={() => navigate('/home')} className="text-[#655dc6] gap-2" data-testid="back-btn">
            <House size={20} />
            {t('back')}
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-semibold text-[#655dc6] mb-6">{t('dashboard')}</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => {
            const clickable = !!(cardBucket[stat.label] || cardNavigate[stat.label]);
            return (
            <Card
              key={index}
              onClick={() => clickable && handleCardClick(stat)}
              className={`border border-[#C4BFF0] hover:-translate-y-1 hover:shadow-lg transition-all duration-200 ${clickable ? 'cursor-pointer' : ''}`}
              data-testid={`stat-card-${stat.label}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#655dc6]/70 mb-1">{t(stat.label)}</p>
                    <p className="text-3xl font-semibold text-[#655dc6]">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <stat.icon size={28} weight="duotone" className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );})}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Department Distribution */}
          <Card className="border border-[#C4BFF0]" data-testid="department-chart">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-[#655dc6]">
                {t('department')} Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {departmentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart margin={{ top: 20, right: 60, bottom: 50, left: 60 }}>
                    <Pie
                      data={departmentData}
                      cx="50%"
                      cy="45%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
                      label={({ cx, cy, midAngle, outerRadius: oR, name, value, fill }) => {
                        // Custom label rendered OUTSIDE the donut with leader
                        // line — prevents overlap with the chart ring.
                        const RADIAN = Math.PI / 180;
                        const radius = oR + 22;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text
                            x={x}
                            y={y}
                            fill={fill || '#374151'}
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            fontSize={11}
                            fontWeight={500}
                          >
                            {`${name}: ${value}`}
                          </text>
                        );
                      }}
                    >
                      {departmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      iconType="circle"
                      wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-[#655dc6]/60">
                  {t('no_data')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attendance Overview */}
          <Card className="border border-[#C4BFF0]" data-testid="attendance-chart">
            <CardHeader>
              <CardTitle className="text-lg font-medium text-[#655dc6]">
                {t('attendance')} Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    { name: t('present_today'), value: stats?.present_today || 0 },
                    { name: t('absent_today'), value: stats?.absent_today || 0 },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#C4BFF0" />
                  <XAxis dataKey="name" tick={{ fill: '#655dc6', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#655dc6', fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#655dc6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Drill-down modal — shows the employees that fall into the
          clicked stat bucket so admins can see who is present/absent/etc. */}
      <Dialog open={bucketModal.open} onOpenChange={(o) => setBucketModal(m => ({ ...m, open: o }))}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" dir={direction} data-testid="bucket-modal">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center justify-between gap-3 pe-8">
              <span>{bucketModal.label}</span>
              <span className="text-sm font-normal text-[#655dc6]/70 whitespace-nowrap">
                {bucketModal.loading ? '...' : `${bucketModal.list.length} ${t('employees') || ''}`}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-2 px-2">
            {bucketModal.loading ? (
              <div className="py-10 text-center text-[#655dc6]/60">{t('loading') || '...'}</div>
            ) : bucketModal.list.length === 0 ? (
              <div className="py-10 text-center text-[#655dc6]/60">{t('no_data')}</div>
            ) : (
              <table className="w-full text-sm" data-testid="bucket-table">
                <thead className="sticky top-0 bg-[#F2F0FA] text-[#655dc6]">
                  <tr>
                    <th className="text-start px-3 py-2 font-medium">{t('employee_number')}</th>
                    <th className="text-start px-3 py-2 font-medium">{language === 'ar' ? 'الاسم' : 'Name'}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('department')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('position')}</th>
                    {bucketModal.bucket === 'present' && (
                      <th className="text-start px-3 py-2 font-medium">{t('clock_in') || 'الحضور'}</th>
                    )}
                    {(bucketModal.bucket === 'off_site' || bucketModal.bucket === 'not_yet_due') && (
                      <th className="text-start px-3 py-2 font-medium">{t('notes') || 'ملاحظات'}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bucketModal.list.map((emp, i) => (
                    <tr key={emp.id} className={i % 2 ? 'bg-white' : 'bg-[#F8F7FD]'} data-testid={`bucket-row-${emp.id}`}>
                      <td className="px-3 py-2 text-[#655dc6]">{emp.employee_number || '-'}</td>
                      <td className="px-3 py-2 text-[#1F2937]">{language === 'ar' ? (emp.name || emp.name_en) : (emp.name_en || emp.name)}</td>
                      <td className="px-3 py-2 text-[#475569]">{getDeptLabel(emp.department, language) || '-'}</td>
                      <td className="px-3 py-2 text-[#475569]">{emp.position || '-'}</td>
                      {bucketModal.bucket === 'present' && (
                        <td className="px-3 py-2 text-[#059669]">{emp.clock_in || '-'}</td>
                      )}
                      {(bucketModal.bucket === 'off_site' || bucketModal.bucket === 'not_yet_due') && (
                        <td className="px-3 py-2 text-[#475569]">{emp.reason || emp.shift_start || '-'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
